import { useMutation } from '@tanstack/react-query';
import { toaster } from 'components/v2/toast';
import { useCallback, useState } from 'react';
import { searchEpds, updateAgent } from 'services/api/adminApi';
import { PoorMansError } from 'util/utils';
import { AdminSearchEpdRequest, AdminEpdSearchResult } from '../../../../types/types';

export function useEpdSearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<AdminSearchEpdRequest | null>(null);
  const [searchResult, setSearchResult] = useState<{
    items: AdminEpdSearchResult[];
    totalCount: number;
  } | null>(null);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });

  const { mutateAsync: fetchEpds } = useMutation({
    mutationFn: searchEpds,
  });

  const { mutateAsync: updateAgentMutation } = useMutation({
    mutationFn: ({ epdVersionId, agentId }: { epdVersionId: string; agentId: string }) => updateAgent(epdVersionId, agentId),
  });

  const onSearch = useCallback(
    async (formValues: any) => {
      const filters: AdminSearchEpdRequest = { ...formValues, page: 1, pageSize: pagination.pageSize };
      try {
        setIsLoading(true);
        const result = await fetchEpds(filters);
        setSearchResult({ items: result.items, totalCount: result.totalCount });
        setPagination((prev) => ({ ...prev, page: 0 }));
        setCurrentFilter(filters);
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchEpds, pagination.pageSize]
  );

  const onPageChange = useCallback(
    async ({ page, rows }: { page: number; rows: number }) => {
      if (!currentFilter) return;

      const nextPage = page + 1;

      try {
        setIsLoading(true);
        const result = await fetchEpds({
          ...currentFilter,
          page: nextPage,
          pageSize: rows,
        });
        setSearchResult({ items: result.items, totalCount: result.totalCount });
        setPagination({ page: page, pageSize: rows });
      } catch (error) {
        toaster({
          severity: 'error',
          summary: 'Error occurred',
          details: PoorMansError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilter, fetchEpds]
  );

  return {
    isLoading,
    searchResult,
    onSearch,
    pagination,
    onPageChange,
    updateAgentMutation,
  };
}
